/* EmployerRouteCard — official manufacturing / industrial employer route (state-level).
   routeOnly cards (no strict facility proof) render in a clearly labelled "Check official route" style. */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Factory, MapPin, ChevronDown, AlertTriangle, ShieldCheck, Search } from 'lucide-react';
import { OfficialLink, EMPLOYER_CHECK_TEXT } from './shared';

export function EmployerRouteCard({ e }) {
  const [open, setOpen] = useState(false);
  const routeOnly = !!e.routeOnly;
  return (
    <motion.article initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-3xl border-2 transition-all ${routeOnly ? 'bg-gray-50/70 border-dashed border-gray-300' : `bg-white ${open ? 'border-slate-400 shadow-lg' : 'border-gray-100 hover:border-slate-300 hover:shadow-md'}`}`}
      data-testid={`bc-employer-${e.employerId}`}>
      <div className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className={`w-10 h-10 rounded-2xl text-white flex items-center justify-center shrink-0 ${routeOnly ? 'bg-gray-400' : 'bg-gradient-to-br from-slate-600 to-gray-800'}`}><Factory size={18} /></span>
            <div className="min-w-0">
              <h3 className="font-extrabold text-gray-900 text-lg leading-tight">{e.employerName}</h3>
              <p className="text-[11px] font-bold text-slate-600 uppercase tracking-widest mt-0.5">{e.industry}</p>
            </div>
          </div>
          {routeOnly ? (
            <span className="inline-flex items-center gap-1 self-start text-[9px] font-extrabold uppercase tracking-wider text-gray-600 bg-gray-100 border border-gray-300 px-2 py-0.5 rounded-full whitespace-normal" data-testid={`bc-employer-badge-${e.employerId}`}>
              <Search size={10} className="shrink-0" /> Check official route · not a verified opening
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 self-start text-[9px] font-extrabold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full whitespace-normal" data-testid={`bc-employer-badge-${e.employerId}`}>
              <ShieldCheck size={10} className="shrink-0" /> Official route & locality verified
            </span>
          )}
        </div>
        <div className="mt-3">
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500 mb-1">{routeOnly ? 'Locality to check' : 'Verified locality'}</p>
          <div className="flex flex-wrap gap-1.5" data-testid={`bc-employer-localities-${e.employerId}`}>
            {e.localities.map((l) => (
              <span key={l} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-white text-gray-700 border border-gray-200 px-2 py-0.5 rounded-full"><MapPin size={10} /> {l}</span>
            ))}
          </div>
        </div>
        <p className="mt-4 text-xs text-amber-900 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-xl p-3 leading-relaxed">
          <AlertTriangle size={13} className="shrink-0 mt-0.5" /> {e.warning}
        </p>
        <p className="mt-2 text-xs font-bold text-gray-800" data-testid={`bc-employer-check-${e.employerId}`}>{EMPLOYER_CHECK_TEXT}</p>
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          {e.officialFacilityEvidenceUrl
            ? <OfficialLink href={e.officialFacilityEvidenceUrl} label="Facility / locality evidence" tone="outline" small testId={`bc-employer-evidence-${e.employerId}`} />
            : <span className="text-[11px] text-gray-500">No facility evidence link — confirm location on the official portal.</span>}
          <OfficialLink href={e.officialCareerUrl} label="Open official career route" tone={routeOnly ? 'outline' : 'emerald'} testId={`bc-employer-cta-${e.employerId}`} />
        </div>
        {!routeOnly && e.ctaText && <p className="mt-2 text-[10px] text-gray-400 text-right">{e.ctaText}</p>}
      </div>

      {!routeOnly && e.roleAreasToExplore.length > 0 && (
        <div className="border-t border-gray-100">
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
            className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-extrabold text-slate-700 hover:bg-slate-50 rounded-b-3xl transition-colors text-left gap-2"
            data-testid={`bc-employer-roles-toggle-${e.employerId}`}>
            <span>Role areas to explore — not confirmed openings ({e.roleAreasToExplore.length})</span>
            <ChevronDown size={16} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="px-5 pb-5 grid sm:grid-cols-2 gap-3">
                  {e.roleAreasToExplore.map((r) => (
                    <div key={r.rolePreviewId} className="rounded-2xl border border-gray-200 bg-white p-4" data-testid={`bc-employer-role-${r.rolePreviewId}`}>
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">Role area to explore</span>
                      <h4 className="font-extrabold text-gray-900 text-sm mt-2">{r.title}</h4>
                      <p className="text-xs text-gray-700 mt-1 leading-relaxed">{r.description}</p>
                      <p className="text-[11px] text-amber-900 mt-2 leading-relaxed">{r.availabilityNotice}</p>
                      <div className="mt-3"><OfficialLink href={e.officialCareerUrl} label="Open official career route" small tone="outline" /></div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.article>
  );
}
